/* Loading the material.
   ------------------------------------------------------------------------
   CONTRACT — owned by the foundation. See PLAN.md before changing it.
   ------------------------------------------------------------------------

   Content is JSON. It lives in /content as files in git, because it is the one
   thing in this project that is genuinely written and therefore wants review,
   diff and history — see PLAN.md. It is nonetheless *loaded over HTTP*, never
   imported: should it ever move into the database behind an editor, this file
   changes and nothing else does.

   Two origins, one shape:
     development   /content/...      served from disk by vite.config.ts
     production    /api/content/...  served by the API */

import type { Doc, IndexEntry, Settings, Taxonomy } from "./types";
import type { Handout } from "./handout";

const BASE = import.meta.env.DEV ? "/content" : "/api/content";

export class ContentError extends Error {
  constructor(readonly path: string, readonly status: number) {
    super(`content ${path} failed with ${status}`);
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: "same-origin" });
  if (!res.ok) throw new ContentError(path, res.status);
  return (await res.json()) as T;
}

/* The catalogue draws grids of up to sixty tiles. Fetching sixty documents to
   do that would be absurd, so the index carries exactly what a tile needs and
   nothing else; the document is fetched when one is opened. */
export const loadIndex    = () => getJson<IndexEntry[]>("/index.json");
export const loadTaxonomy = () => getJson<Taxonomy>("/taxonomy.json");
export const loadDoc      = (id: string) => getJson<Doc>(`/docs/${id}.json`);
export const loadHandout  = (id: string) => getJson<Handout>(`/handouts/${id}.json`);
export const loadStrings  = (locale: string) => getJson<Record<string, string>>(`/locales/${locale}.json`);

/* The one thing under /content that is not JSON: the interactive application of
   a lesson, one self-contained HTML file. It is fetched as text and never
   navigated to — it runs inside a sandboxed frame and is handed over as a file
   from the same text, so it is read once and used twice. */
export async function loadApp(name: string): Promise<string> {
  const path = `/apps/${name}.html`;
  const res = await fetch(`${BASE}${path}`, { credentials: "same-origin" });
  if (!res.ok) throw new ContentError(path, res.status);
  return res.text();
}

/* ---------- server state ----------

   Not content, but the same fetch discipline. Feature switches are read by
   everyone and written only by an administrator; the browser holds no copy that
   outlives the session and no localStorage flag decides anything. */

export const loadSettings = () => fetch("/api/settings", { credentials: "same-origin" })
  .then((r) => (r.ok ? (r.json() as Promise<Settings>) : { features: {} }))
  /* The portal must render with the generator and the file maker simply absent
     rather than fail to render at all, so an unreachable API is "everything
     off", never an error page. */
  .catch(() => ({ features: {} } as Settings));
